import { create } from "zustand";
import { persist } from "zustand/middleware";

export const UI_SCALE_VALUES = [0.75, 0.8, 0.9, 1, 1.125, 1.25, 1.5] as const;

export type UiScale = (typeof UI_SCALE_VALUES)[number];

export const DEFAULT_UI_SCALE: UiScale = 1;

const SCALE_SET = new Set<number>(UI_SCALE_VALUES);

export function isUiScale(n: unknown): n is UiScale {
  return typeof n === "number" && SCALE_SET.has(n);
}

export function applyUiScaleToDocument(scale: UiScale): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-ui-scale", String(scale));
}

export const UI_SCALE_OPTIONS: { value: UiScale; label: string }[] = [
  { value: 0.75, label: "75%" },
  { value: 0.8, label: "80%" },
  { value: 0.9, label: "90%" },
  { value: 1, label: "100%" },
  { value: 1.125, label: "112.5%" },
  { value: 1.25, label: "125%" },
  { value: 1.5, label: "150%" },
];

interface UiScaleState {
  scale: UiScale;
  setScale: (scale: UiScale) => void;
}

export const useUiScaleStore = create<UiScaleState>()(
  persist(
    (set) => ({
      scale: DEFAULT_UI_SCALE,
      setScale: (scale) => {
        set({ scale });
        applyUiScaleToDocument(scale);
      },
    }),
    {
      name: "ui-scale",
      partialize: (s) => ({ scale: s.scale }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const sc = state.scale;
        if (!isUiScale(sc)) {
          state.scale = DEFAULT_UI_SCALE;
        }
        applyUiScaleToDocument(state.scale);
      },
    },
  ),
);
